import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCalendarAlt, FaEdit, FaEye, FaPlus, FaSearch } from "react-icons/fa";
import { useTable, useSortBy, useGlobalFilter, usePagination } from "react-table";
import "./DriverChecklistList.css";

const DriverChecklistList = () => {
  const [checklists, setChecklists] = useState([]);
  const [loggedInDriver] = useState("");
  const [userRole, setUserRole] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); 
  const navigate = useNavigate();
  const currentPeriod = new Date().toLocaleString("en-GB", { month: "long", year: "numeric" });

  const formatChecklistDate = (value) => {
    if (!value) return "—";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value;
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
  };

  // Add debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFilter(searchInput); 
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const fetchChecklists = async () => {
      try {
        const token = localStorage.getItem("token");
        const role = localStorage.getItem("userrole");

        const query = new URLSearchParams();
        if (statusFilter !== "all") query.set("status", statusFilter);
        let url = `${process.env.REACT_APP_BASE_API_URL}/driver-checklists?${query.toString()}`;

        if (role === "driver") {
          const driverName = localStorage.getItem("driverName");
          const driverId = localStorage.getItem("driverId");
          if (driverName && driverId) {
            url = `${
              process.env.REACT_APP_BASE_API_URL
            }/driver-checklists/${encodeURIComponent(
              driverName
            )}/${encodeURIComponent(driverId)}?${query.toString()}`;
          }
        }

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // handle wrapped response: { success: true, data: [...] }
        let data = response.data;
        if (data && data.data) data = data.data;
        if (data && !Array.isArray(data)) data = [];

        // normalize all keys to lowercase
        const normalized = (Array.isArray(data) ? data : []).map((item) => {
          const norm = {};
          Object.keys(item || {}).forEach((k) => {
            norm[k.toLowerCase()] = item[k];
          });
          return norm;
        });

        setChecklists(normalized);
      } catch (error) {
        console.error("Error fetching checklists:", error);
        setChecklists([]);
      }
    };

    fetchChecklists();
  }, [statusFilter]);

  const filteredChecklists = useMemo(() => {
    return checklists.filter((checklist) =>
      globalFilter
        ? Object.values(checklist)
            .join(" ")
            .toLowerCase()
            .includes(globalFilter.toLowerCase())
        : true
    );
  }, [checklists, globalFilter]);

  const columns = useMemo(
    () => [
      { Header: "Checklist No", accessor: "sr_no" },
      { Header: "Driver Name", accessor: "drivername" },
      { Header: "Badge Number", accessor: "badgenumber" },
      { Header: "Fleet Number", accessor: "fleetnumber" },
      { Header: "KM Reading", accessor: "kmreading" },
      { Header: "Location", accessor: "location" },
      {
        Header: "Date",
        accessor: "checklistdate",
        Cell: ({ value }) => {
          return formatChecklistDate(value);
        },
      },
      {
        Header: "Status",
        accessor: "checkliststatus",
        Cell: ({ value }) => (
          <span className={`status-badge ${value}`}>
            {value ? value.toUpperCase() : "N/A"}
          </span>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div>
            <FaEye
              className="action-icon view-icon"
              title="View"
              onClick={() => handleView(row.original.sr_no)}
            />
            <FaEdit
              className="action-icon edit-icon"
              title="Edit"
              onClick={() => handleEdit(row.original.sr_no)}
            />
          </div>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow,
    canPreviousPage, canNextPage, previousPage, nextPage, pageCount, gotoPage, setPageSize, state: { pageIndex, pageSize } } =
    useTable(
      {
        columns,
        data: filteredChecklists,
        initialState: { pageSize: 40 },
      },
      useGlobalFilter,
      useSortBy,
      usePagination
    );

  const handleView = (id) => {
    navigate(`/driver-checklists/view/${encodeURIComponent(id)}`);
  };

  const handleEdit = (id) => {
    navigate(`/driver-checklists/edit/${encodeURIComponent(id)}`);
  };

  const handleCreateNew = () => {
    navigate("/driver-checklists/new");
  };

  useEffect(() => {
    const role = localStorage.getItem("userrole");
    setUserRole(role);
  }, []);

  return (
    <div className="dcl-page">
      <header className="dcl-modern-header">
        <button className="dcl-back-button" onClick={() => navigate("/main")} aria-label="Back to main page">
          <FaArrowLeft />
        </button>
        <div className="dcl-title-block">
          <h1>Driver Vehicle Checklists</h1>
          <p><FaCalendarAlt /> {currentPeriod} · Current month records</p>
        </div>
        <div className="dcl-header-filter">
          <label htmlFor="checklist-status">Status</label>
          <select
            id="checklist-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter-dropdown"
          >
            <option value="all">All Status</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
          </select>
        </div>
      </header>

      <div className="driver-checklist-list-container">
        {/* Header Section */}
        <div className="driver-checklist-list-header">
          <div className="left-button">
            {userRole === "driver" && (
              <button
                onClick={handleCreateNew}
                className="create-new-checklist-button"
              >
                <FaPlus className="add-icon" /> Create New Checklist
              </button>
            )}
          </div>
          <div className="right-button">
            <FaSearch className="dcl-search-icon" />
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="search-inputchecklist"
            />
          </div>
        </div>

        <div
          className="driverchecklisttable-container"
        >
          <table {...getTableProps()} className="driver-checklist-list-table">
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr key={headerGroupKey} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: columnKey, ...columnProps } =
                        column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th key={columnKey} {...columnProps}>
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " 🔽"
                                : " 🔼"
                              : ""}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps} style={{ height: "15px" }}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="dcl-mobile-list">
          {page.map((row) => {
            const item = row.original;
            return <article key={`mobile-${item.sr_no}`}>
              <div className="dcl-card-head"><div><small>CHECKLIST</small><strong>{item.sr_no}</strong></div><span className={`status-badge ${item.checkliststatus}`}>{item.checkliststatus?.toUpperCase()}</span></div>
              <h3>{item.drivername}</h3>
              <div className="dcl-card-grid"><span>Badge<b>{item.badgenumber}</b></span><span>Fleet<b>{item.fleetnumber}</b></span><span>KM Reading<b>{item.kmreading}</b></span><span>Date<b>{formatChecklistDate(item.checklistdate)}</b></span></div>
              <div className="dcl-card-actions"><button onClick={() => handleView(item.sr_no)}><FaEye /> View</button><button onClick={() => handleEdit(item.sr_no)}><FaEdit /> Edit</button></div>
            </article>;
          })}
        </div>
        <div className="dcl-pagination">
          <span>Showing {filteredChecklists.length ? pageIndex * pageSize + 1 : 0}–{Math.min((pageIndex + 1) * pageSize, filteredChecklists.length)} of {filteredChecklists.length}</span>
          <div><button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>First</button><button onClick={previousPage} disabled={!canPreviousPage}>Previous</button><b>Page {pageIndex + 1} of {pageCount || 1}</b><button onClick={nextPage} disabled={!canNextPage}>Next</button></div>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value="40">40 rows</option><option value="60">60 rows</option><option value="100">100 rows</option></select>
        </div>
      </div>
    </div>
  );
};

export default DriverChecklistList;
